import { useInView } from '../hooks/useInView';

const LEVELS = ['Very Little', 'Limited', 'Basic', 'Good', 'Deep & Nuanced'];
const levelLabel = (v) => LEVELS[Math.round(v) - 1] || 'Basic';

const identities = [
  {
    label: 'Caste',
    before: 3.39, after: 4.06, delta: '+0.67', pct: 54,
    beforeLevel: 'Basic understanding',
    afterLevel: 'Good understanding',
    context: 'Caste continues to shape access to justice across India. Students moved from surface-level awareness to genuine understanding of how caste intersects with law, opportunity, and everyday life.',
    icon: '⚖️',
  },
  {
    label: 'Gender',
    before: 3.63, after: 4.13, delta: '+0.50', pct: 44,
    beforeLevel: 'Good understanding',
    afterLevel: 'Stronger good understanding',
    context: 'Gender-based discrimination remains one of the most pervasive justice challenges. Students deepened their understanding of how gender shapes lived experiences — not just in theory but in real community contexts.',
    icon: '♀♂',
  },
  {
    label: 'Religion',
    before: 3.43, after: 3.93, delta: '+0.50', pct: 41,
    beforeLevel: 'Basic understanding',
    afterLevel: 'Good understanding',
    context: 'Religious identity affects legal rights, personal freedoms, and social belonging. JI 101 created space for students to engage with these realities openly — often for the first time in an academic setting.',
    icon: '✦',
  },
];

const scaleStops = ['Very Little', 'Limited', 'Basic', 'Good', 'Deep & Nuanced'];

export default function IdentitySection() {
  const [ref, inView] = useInView({ threshold: 0.08 });
  return (
    <section id="identity" className="section">
      <div className="container" ref={ref}>

        <div className={`reveal ${inView?'visible':''}`} style={{ marginBottom:'3.5rem' }}>
          <p className="eyebrow">Social Identity Awareness</p>
          <h2 className="h2">Caste. Gender. Religion.<br />Head on.</h2>
          <p className="body-lg" style={{ maxWidth:'560px', marginTop:'0.75rem' }}>
            JI 101 confronts what most classrooms avoid. Students rated their understanding on a 5-point scale, from <em>"I have very little understanding"</em> to <em>"I have a deep and nuanced understanding"</em> — before and after the workshop.
          </p>
        </div>

        {/* Scale legend */}
        <div className={`reveal ${inView?'visible':''}`} style={{ marginBottom:'2.5rem', transitionDelay:'0.05s' }}>
          <div className="nb-card-surface" style={{ padding:'1rem 1.5rem', display:'flex', alignItems:'center', gap:'0', flexWrap:'wrap' }}>
            <span style={{ fontFamily:'var(--font-head)', fontSize:'0.6rem', fontWeight:700, letterSpacing:'0.12em', textTransform:'uppercase', color:'var(--muted)', marginRight:'1rem' }}>Scale:</span>
            {scaleStops.map((s, i) => (
              <div key={i} style={{ display:'flex', alignItems:'center', gap:'0' }}>
                <div style={{ display:'flex', flexDirection:'column', alignItems:'center', padding:'0 0.75rem' }}>
                  <div style={{ width:24, height:24, border:'2px solid var(--black)', borderRadius:2, background: i===0?'var(--white)': i===4?'var(--accent)':'var(--white)', display:'flex', alignItems:'center', justifyContent:'center', fontFamily:'var(--font-head)', fontSize:'0.65rem', fontWeight:700 }}>{i+1}</div>
                  <div style={{ fontFamily:'var(--font-head)', fontSize:'0.52rem', fontWeight:600, textAlign:'center', color:'var(--muted)', marginTop:'4px', maxWidth:'60px' }}>{s}</div>
                </div>
                {i < 4 && <div style={{ width:'20px', height:'2px', background:'#DDD' }} />}
              </div>
            ))}
          </div>
        </div>

        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(300px, 1fr))', gap:'1.5rem' }}>
          {identities.map((item, i) => (
            <div key={i} className={`reveal nb-card ${inView?'visible':''}`} style={{ padding:'0', overflow:'hidden', transitionDelay:`${i*0.1}s` }}>

              {/* Card header */}
              <div style={{ padding:'1.5rem 1.75rem', borderBottom:'2.5px solid var(--black)', display:'flex', justifyContent:'space-between', alignItems:'center', background:'var(--surface)' }}>
                <div>
                  <div style={{ fontSize:'1.2rem', marginBottom:'4px' }}>{item.icon}</div>
                  <div style={{ fontFamily:'var(--font-head)', fontSize:'1.1rem', fontWeight:700 }}>{item.label}</div>
                  <div style={{ fontFamily:'var(--font-head)', fontSize:'0.6rem', fontWeight:700, letterSpacing:'0.12em', textTransform:'uppercase', color:'var(--muted)' }}>Identity Understanding</div>
                </div>
                <span className="tag tag-accent">{item.pct}% improved</span>
              </div>

              {/* Before / After visual */}
              <div style={{ padding:'1.75rem', borderBottom:'2.5px solid var(--black)' }}>

                {/* Visual bar track — BOTH before and after shown */}
                <div style={{ marginBottom:'1.5rem' }}>
                  <div style={{ display:'flex', justifyContent:'space-between', marginBottom:'6px' }}>
                    <span style={{ fontFamily:'var(--font-head)', fontSize:'0.6rem', fontWeight:700, letterSpacing:'0.12em', textTransform:'uppercase', color:'var(--before-color)' }}>Before</span>
                    <span style={{ fontFamily:'var(--font-head)', fontSize:'0.6rem', fontWeight:700, letterSpacing:'0.12em', textTransform:'uppercase', color:'var(--accent)' }}>After</span>
                  </div>

                  {/* Before bar — gray */}
                  <div style={{ position:'relative', marginBottom:'10px' }}>
                    <div style={{ height:'12px', background:'#EBEBEB', border:'1.5px solid #CCC', borderRadius:2, overflow:'hidden' }}>
                      <div style={{
                        height:'100%',
                        width: inView ? `${(item.before/5)*100}%` : '0%',
                        background:'var(--before-color)',
                        transition:'width 1.2s cubic-bezier(0.25,1,0.5,1)',
                        transitionDelay:`${i*0.1+0.3}s`,
                        borderRadius:2,
                      }} />
                    </div>
                    <div style={{ position:'absolute', right:0, top:'50%', transform:'translate(calc(100% + 8px), -50%)', fontFamily:'var(--font-head)', fontSize:'0.85rem', fontWeight:700, color:'var(--before-color)' }}>{item.before.toFixed(1)}</div>
                  </div>

                  {/* After bar — teal */}
                  <div style={{ position:'relative' }}>
                    <div style={{ height:'12px', background:'#E0F9F8', border:'1.5px solid var(--accent)', borderRadius:2, overflow:'hidden' }}>
                      <div style={{
                        height:'100%',
                        width: inView ? `${(item.after/5)*100}%` : '0%',
                        background:'var(--accent)',
                        transition:'width 1.2s cubic-bezier(0.25,1,0.5,1)',
                        transitionDelay:`${i*0.1+0.5}s`,
                        borderRadius:2,
                      }} />
                    </div>
                    <div style={{ position:'absolute', right:0, top:'50%', transform:'translate(calc(100% + 8px), -50%)', fontFamily:'var(--font-head)', fontSize:'0.85rem', fontWeight:700, color:'var(--accent)' }}>{item.after.toFixed(1)}</div>
                  </div>
                </div>

                {/* Level shift */}
                <div style={{ display:'flex', alignItems:'center', gap:'8px', flexWrap:'wrap' }}>
                  <span style={{ fontFamily:'var(--font-head)', fontSize:'0.72rem', fontWeight:600, color:'var(--before-color)', textDecoration:'line-through' }}>{item.beforeLevel}</span>
                  <svg width="16" height="10" viewBox="0 0 16 10"><path d="M0 5h12M9 1l4 4-4 4" stroke="var(--accent)" strokeWidth="1.5" fill="none" strokeLinecap="round"/></svg>
                  <span style={{ fontFamily:'var(--font-head)', fontSize:'0.72rem', fontWeight:700, color:'var(--accent)' }}>{item.afterLevel}</span>
                </div>

                {/* Delta badge */}
                <div style={{ marginTop:'1rem', display:'inline-flex', alignItems:'center', gap:'6px', background:'var(--accent)', border:'2px solid var(--black)', borderRadius:2, padding:'4px 12px' }}>
                  <span style={{ fontFamily:'var(--font-head)', fontSize:'0.75rem', fontWeight:800, color:'var(--black)' }}>Average shift: {item.delta}</span>
                </div>
              </div>

              {/* Context */}
              <div style={{ padding:'1.25rem 1.75rem' }}>
                <p style={{ fontFamily:'var(--font-body)', fontSize:'0.82rem', color:'var(--muted)', lineHeight:1.65 }}>{item.context}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
